import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  BarChart3,
  CheckCircle2,
  MessageSquareText,
  TrendingUp,
  Trophy,
  X,
} from 'lucide-react';

const toList = (value) => (Array.isArray(value) ? value : value ? [value] : []);

const Popup = ({ open, onClose, feedback = {}, grade: gradeFallback = '' }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const grade = feedback.grade || gradeFallback || '—';
  const strengths = toList(feedback.strengths);
  const improvements = toList(feedback.improvements);
  const summary = feedback.summary || '';

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div className="case-feedback-layer" role="presentation" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="case-feedback-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="case-feedback-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="case-feedback-header">
          <div>
            <span><MessageSquareText size={16} /> AI feedback</span>
            <h2 id="case-feedback-title">Your case review is ready.</h2>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close feedback">
            <X size={21} />
          </button>
        </header>

        <div className="case-feedback-body">
          <section className="case-feedback-grade">
            <div><Trophy size={26} /><span>Overall grade</span></div>
            <strong>{grade}</strong>
            <p>Use the detail below to decide what to repeat and what to sharpen in your next case.</p>
          </section>

          <div className="case-feedback-grid">
            <section className="case-feedback-panel is-strength">
              <div><CheckCircle2 size={20} /><h3>What worked</h3></div>
              {strengths.length ? (
                <ul>{strengths.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
              ) : <p>No specific strengths were returned for this attempt.</p>}
            </section>

            <section className="case-feedback-panel is-improvement">
              <div><TrendingUp size={20} /><h3>What to improve</h3></div>
              {improvements.length ? (
                <ul>{improvements.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
              ) : <p>No specific improvements were returned for this attempt.</p>}
            </section>
          </div>

          {summary && (
            <section className="case-feedback-summary">
              <span>Coach’s summary</span>
              <p>{summary}</p>
            </section>
          )}
        </div>

        <footer className="case-feedback-actions">
          <Link to="/dashboard" className="is-secondary"><BarChart3 size={17} /> View performance</Link>
          <Link to="/cases" className="is-primary">Try another case <ArrowUpRight size={18} /></Link>
        </footer>
      </section>
    </div>
  );
};

export default Popup;
